import { Box, Center, Flex, Tab, TabIndicator, TabList, TabPanel, TabPanels, Tabs, Text, useToast } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useQueryClient } from "react-query";

import { ContactDto, CorporationDto, UserDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { userName } from "@bcasmart/doxecure-commons/doxecureFunctions";
import { ContactStatusType, ContactType, LockedAccount, UserProfile } from "@bcasmart/doxecure-commons/doxecureTypes";
import { Nullable, UUID } from "foundation-ts/types";
import { $email, $ok } from "foundation-ts/commons";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { ContactsList } from "@components/ContactsList";
import { ContactForm, NewPersonFormValues } from "@/components/ContactForm/ContactForm";
import { G1Button } from "@components/G1Button";
import { SearchUsers } from "@components/admin/SearchUsers";
import { TagFilterBar } from "@components/Tags/TagFilterBar";
import { UserParameters } from "@components/UserInfos/UserInfos";
import { ContactsQueryID } from "@hooks/hooksConstants";
import { getCorporationUsers } from "@services/corporations.service";
import { addUserContact } from "@services/users.service";
import { $g1Color } from "@/theme/G1Style";
import { trueOrEmptyCard } from "@utils/functions";
import { G1Icon } from "@utils/icons";
import { defaultErrorToast, defaultSuccessToast } from "@utils/toast";
import { NeededPersonRole } from "@utils/TypesAndConstants";

interface PersonPanelProps {
    profileId: UUID;
    connectedUserProfile: UserDto;
    role: NeededPersonRole;
    blacklist?: Set<UUID>;
    canaddme?: boolean;
    enableAddUserTab?: boolean;
    enableAllUsersTab?: boolean;
    lockedAccount?: LockedAccount;
    onSelectPersonItemAs: (item: PersonItem, role: NeededPersonRole) => void;
    onBack: () => void;
}
export interface PersonItem {
    contactID?: UUID;
    person: UserDto;
    isConnectedUser?: boolean;
    contactStatus?: ContactStatusType;
};

export const PersonPanel = (props: PersonPanelProps) => {
    const { profileId, role, connectedUserProfile, blacklist, canaddme = false, enableAddUserTab = true, enableAllUsersTab = true, lockedAccount, onSelectPersonItemAs, onBack } = props;
    const [corporateUsers, setCorporateUsers] = useState<Nullable<UserParameters[]>>(null);
    const [workedCorporateUsers, setWorkedCorporateUsers] = useState<Nullable<UserParameters[]>>(null);
    const [usualContactsEmails, setUsualContactsEmails] = useState<string[]>([]);
    const [allContactsEmails, setAllContactsEmails] = useState<string[]>([]);
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const queryClient = useQueryClient();
    const blacklistemail = useMemo(() => {
        const emails = new Set([
            ...usualContactsEmails,
            ...allContactsEmails,
            ...(corporateUsers?.map(u => u.email) ?? [])
        ]);
        return Array.from(emails);
    }, [usualContactsEmails, allContactsEmails, corporateUsers]);
    const toast = useToast();
    
    const isCorporate = connectedUserProfile.userFlags?.profile === UserProfile.AdministratorCorporate || connectedUserProfile.userFlags?.profile === UserProfile.UserCorporate;
    const userTags = connectedUserProfile?.tags ?? {};
    const roles: Record<NeededPersonRole, string[]> = {
        Sender: ["comme expéditeur", "nouvel expéditeur", "Expéditeurs"],
        Signer: ["aux signataires", "nouveau signataire", "Signataires"],
        Approver: ["", "", "Approbateurs"],
        Recipient: ["", "nouveau destinataire", "Destinataires"]
    };
    const addConnectedUserAsTitle = `M'ajouter ${roles[role][0]}`;
    const createTitleBtn = roles[role][1];
    const tabTitle = roles[role][2];
    const contactsType: Record<NeededPersonRole, ContactType> = {
        Sender: ContactType.UsualExpeditor,
        Signer: ContactType.UsualSigner,
        Approver: ContactType.UsualApprobator,
        Recipient: ContactType.UsualSigner
    }
    const usualContactsType = contactsType[role];

    const handleUsualContactsLoaded = (contacts: ContactDto[]) => {
        setUsualContactsEmails(contacts.map(c => c.contact.email));
    };

    const handleAllContactsLoaded = (contacts: ContactDto[]) => {
        setAllContactsEmails(contacts.map(c => c.contact.email));
    };

    const handleSelectPersonItem = (item: PersonItem) => {
        onSelectPersonItemAs(item!, role);
    } ;

    const handleSelectContactItem = (item: ContactDto) => {
        handleSelectPersonItem({ person: item.contact, contactID: item.apid, contactStatus: item.status });
    } ;

    const handleSelectCollaboratorItem = (userId: UUID) => {
        const user = corporateUsers!.filter(user => user.apid === userId).first() ;
        if ($ok(user)) {
            handleSelectPersonItem({ person: user!, contactID: userId! }) ;
        }
    } ;

    const handleSearchCorporationUser = async (search?: Nullable<string>): Promise<void> => {
        const users = $ok(search) ? corporateUsers?.filter(userDto => userDto.lastName.includes(search!.toUpperCase()) || userDto.email === search?.toLocaleLowerCase()) : corporateUsers ;
        setWorkedCorporateUsers(users?.map(userDto => { return {
            firstName: userDto.firstName,
            lastName: userDto.lastName,
            email: userDto.email,
            apid: userDto.apid,
            userFlags: userDto.userFlags
        }})) ;
    } ;

    const onAddContact: SubmitHandler<NewPersonFormValues> = async (values): Promise<boolean> => {
        try {
            const mail = $email(values.email);
            if (!$ok(mail)) { throw 'Bad email'; }
            let newContact = await addUserContact(profileId, { email: mail!, lastName: values.lastName, firstName: values.firstName, phone: values.phone });
            if (!$ok(newContact) || !$ok(newContact.apid)) { throw 'Bad contact creation'; }
            toast(defaultSuccessToast(`${userName(newContact.contact)} ajouté.`));
            queryClient.invalidateQueries([ContactsQueryID, profileId]);
            onSelectPersonItemAs({ person: newContact.contact, contactID: newContact.apid! }, role);
            return true;
        } catch(error) {
            toast(defaultErrorToast("Les informations renseignées ne sont pas valides."));
        }
        return false;
    } ;

    useEffect(() => {
        const setCorporationUsersList = async (connectedUserId: UUID, corporations: CorporationDto[]) => {
            const users: UserParameters[] = [] ;
            for (let corporation of corporations) {
                const usersDto = await getCorporationUsers(corporation.apid) ;
                if ($ok(usersDto)) {
                    let filteredUsers = usersDto!.filter(userDto => (userDto.apid !== connectedUserId) && !blacklist?.has(userDto.apid!));
                    if (role === NeededPersonRole.Approver) {
                        filteredUsers = filteredUsers.filter(userDto => userDto.userFlags?.profile !== UserProfile.Contact);
                    }
                    users.push(...filteredUsers.map(userDto => {
                            return {
                                firstName: userDto.firstName,
                                lastName: userDto.lastName,
                                email: userDto.email,
                                apid: userDto.apid,
                                userFlags: userDto.userFlags
                            }}
                    ));
                }
            }
            setWorkedCorporateUsers(users) ;
            setCorporateUsers(users) ;
        }

        if (isCorporate) {
            setCorporationUsersList(connectedUserProfile.apid!, connectedUserProfile.corporations!);
        } else {
            setWorkedCorporateUsers([]) ;
            setCorporateUsers([]) ;
        }
    }, [isCorporate]);

    return (
        <>
            <ColumnTitle color={$g1Color('global.write')}>
                <Flex alignItems="center" as="button" onClick={onBack} width="100%">
                    <Center color={$g1Color('global.icons.navigation.previous.write')}><G1Icon.Back /></Center>
                    <Text>Retour</Text>
                </Flex>
            </ColumnTitle>
            <Tabs isFitted>
                <TabList backgroundColor={$g1Color('global.tabs.bg')} color={$g1Color('global.tabs.write')}>
                    <Tab fontWeight="semibold">{tabTitle} Usuels</Tab>
                    { enableAllUsersTab && <Tab fontWeight="semibold">Contacts</Tab> }
                    { isCorporate && <Tab fontWeight="semibold">Collaborateurs</Tab> }
                    { enableAddUserTab && <Tab fontWeight="semibold">Ajouter</Tab> }
                </TabList>
                <TabIndicator color={$g1Color('global.tabs.indicator.write')} backgroundColor={$g1Color('global.tabs.indicator.bg')} />
                <TabPanels>
                    <TabPanel>
                        { trueOrEmptyCard(canaddme,(<G1Button component="form" variant="important" title={addConnectedUserAsTitle} width="100%" mb="30px" height="70px" justifyContent="center" fontSize="lg" onClick={() => { handleSelectPersonItem({ person: connectedUserProfile!, isConnectedUser: true }); }} />)) }
                        <ContactsList connectedUserProfile={connectedUserProfile!} blacklist={blacklist} contactType={usualContactsType} onItemClick={handleSelectContactItem} onContactsLoaded={handleUsualContactsLoaded} />
                    </TabPanel>
                    {enableAllUsersTab &&
                        <TabPanel>
                            { trueOrEmptyCard(canaddme,(<G1Button component="form" variant="important" title={addConnectedUserAsTitle} width="100%" mb="30px" height="70px" justifyContent="center" fontSize="lg" onClick={() => { handleSelectPersonItem({ person: connectedUserProfile!, isConnectedUser: true }); }} />)) }
                            {Object.keys(userTags).length > 0 && (
                                <Box mb={3}>
                                    <Text mb={1}>Filtrer par catégorie</Text>
                                    <TagFilterBar
                                        tags={userTags}
                                        selected={selectedTags}
                                        onToggle={(key) => setSelectedTags(prev => {
                                            const next = new Set(prev);
                                            next.has(key) ? next.delete(key) : next.add(key);
                                            return next;
                                        })}
                                    />
                                </Box>
                            )}
                            <ContactsList connectedUserProfile={connectedUserProfile!} blacklist={blacklist} contactType={ContactType.All} search ordered tagFilters={selectedTags} onItemClick={handleSelectContactItem} onContactsLoaded={handleAllContactsLoaded} />
                        </TabPanel>
                    }
                    {isCorporate &&
                        <TabPanel>
                            <SearchUsers users={workedCorporateUsers || []} onSearch={handleSearchCorporationUser} onItemClick={handleSelectCollaboratorItem} />
                        </TabPanel>
                    }
                    {enableAddUserTab &&
                        <TabPanel>
                            <ContactForm mode="create" connectedUserProfile={connectedUserProfile!} blacklist={blacklistemail} lockedAccount={lockedAccount} titleBtn={createTitleBtn} onSubmit={onAddContact} />
                        </TabPanel>
                    }
                </TabPanels>
            </Tabs>
        </>
    ) ;
} ;